import {
  DiscrepancyCheckRequest,
  DiscrepancyCheckResponse,
} from '@/types/po-discrepancies'
import useGetDetail from '../core-hooks/useGetDetail'
import { BaseQueryOptions } from '../query-types'
import { createDetail } from '../request-wrappers'

type UseGetPODiscrepanciesOptions = {
  /** Full check payload — the caller provides everything, so the server skips its own document/relay lookups (fast path). */
  payload?: DiscrepancyCheckRequest
} & Omit<BaseQueryOptions<DiscrepancyCheckResponse>, 'queryKey'>

/**
 * MARK: Action Hook: Check Invoice/PO Discrepancies
 * Runs pd-po's ad-hoc discrepancy check with caller-provided data (the
 * invoice values, POs, and line matches this view already holds). The payload
 * lives in the query key (the `detail` modifiers slot), so any change to it —
 * match created/deleted, PO added/removed, vendor or PO number change —
 * refetches automatically. It's a read modeled as a POST because the request
 * body is the query, so it goes through `useGetDetail` with `createDetail` as
 * the service function rather than a mutation hook.
 */
export function useGetPODiscrepancies({
  payload,
  enabled = true,
  ...options
}: UseGetPODiscrepanciesOptions) {
  return useGetDetail<DiscrepancyCheckResponse>({
    id: 'po-discrepancies',
    queryKey: ['pd-po', 'po-discrepancies', payload],
    serviceFn: ({ axiosOptions }) =>
      createDetail<DiscrepancyCheckRequest, DiscrepancyCheckResponse>({
        url: '/v2/pd-po/invoices/discrepancy-check',
        item: payload as DiscrepancyCheckRequest,
        axiosOptions,
      }),
    ...options,
    enabled: !!payload && enabled,
  })
}
